import React, { useState, useEffect } from "react";
import { FiTrash2, FiEye, FiSearch, FiFilter, FiDownload } from "react-icons/fi";
import { FaTimes, FaFile, FaBuilding, FaMapMarkerAlt, FaUser, FaImage, FaCalendar, FaInfo, FaTag } from "react-icons/fa";
import { useBrands, useDeleteBrand } from "../../hooks/useBrands.ts";
import Pagination from "../../components/Pagination.tsx";
import axios from "axios";
import { showConfirmation } from "../../services/confirmation.ts";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from "../../theme.ts";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
  ColumnDef,
} from "@tanstack/react-table";

import { Brand, BrandDocument } from "../../types/index.ts";

// Local fallback component for broken images
const FallbackImage: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`${className} flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800`}>
    <FaImage className="text-2xl text-gray-400 dark:text-gray-500" />
  </div>
);

const AdminBrands: React.FC = () => {
  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName);
  const { data: brandsData, isLoading: isLoadingBrands, refetch: refetchBrands } = useBrands();
  const deleteBrandMutation = useDeleteBrand();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [search, setSearch] = useState("");
  const [viewBrand, setViewBrand] = useState<Brand | null>(null);
  const [brandDocuments, setBrandDocuments] = useState<BrandDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [isViewDocumentsModalOpen, setIsViewDocumentsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [imageErrors, setImageErrors] = useState<{ [key: string]: boolean }>({});

  const rawData = localStorage.getItem('user');
  const cred = rawData ? JSON.parse(rawData) : null;
  const token = cred?.access;

  const handleDelete = async (id: string) => {
    try {
      await deleteBrandMutation.mutateAsync(id);
      setBrands(brands.filter((brand) => brand.id !== id));
    } catch (err) {
      console.log(err)
    }
  };

  const handleViewBrand = (brand: Brand) => {
    setViewBrand(brand);
  };

  // Handle image error
  const handleImageError = (brandId: string) => {
    setImageErrors(prev => ({ ...prev, [brandId]: true }));
  };

  const fetchBrandDocuments = async (brandId: string) => {
    try {
      setLoadingDocuments(true);
      const response: any = await axios.get(`${process.env.REACT_APP_API_URL}/brands/${brandId}/documents/`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response?.status === 200) {
        setBrandDocuments(response.data);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingDocuments(false);
    }
  };

  useEffect(() => {
    if (brandsData) {
      setBrands(brandsData);
    }
  }, [brandsData]);

  const filteredBrands = brands.filter((brand) =>
    brand?.title?.toLowerCase().includes(search.toLowerCase()) ||
    brand?.location?.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination calculations
  const totalPages = Math.ceil(filteredBrands.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  // TanStack Table column definitions
  const columnHelper = createColumnHelper<Brand>();

  const columns: ColumnDef<Brand, any>[] = [
    columnHelper.accessor('logo', {
      header: 'Logo',
      cell: (info) => {
        const brandId = info.row.original.id;
        return (
          <div className="relative w-16 h-12">
            {imageErrors[brandId] ? (
              <FallbackImage className="w-16 h-12 rounded-lg" />
            ) : (
              <img
                src={info.getValue()}
                alt={`${info.row.original.title} logo`}
                className="w-16 h-12 object-cover rounded-lg shadow-sm transition-all duration-300 hover:shadow-md hover:brightness-105"
                style={{ border: `1px solid ${currentTheme.border}` }}
                onError={() => handleImageError(brandId)}
                loading="lazy"
              />
            )}
          </div>
        );
      },
      enableSorting: false,
    }),
    columnHelper.accessor('title', {
      header: 'Brand Name',
      cell: (info) => (
        <div className="transition-all duration-300 hover:translate-x-1">
          <span className="font-semibold" style={{ color: currentTheme.text }}>{info.getValue()}</span>
          <span className="block text-xs font-mono" style={{ color: currentTheme.textSecondary }}>
            ID: {info.row.original.id.substring(0, 8)}...
          </span>
        </div>
      ),
    }),
    columnHelper.accessor('location', {
      header: 'Location',
      cell: (info) => (
        <div className="flex items-center gap-2 transition-all duration-300 hover:translate-x-1">
          <FaMapMarkerAlt className="text-sm" style={{ color: currentTheme.primary }} />
          <span style={{ color: currentTheme.text }}>{info.getValue()}</span>
        </div>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (props) => {
        const brand = props.row.original;
        return (
          <div className="flex gap-2">
            <button
              onClick={() => showConfirmation(
                'Delete Brand',
                `Are you sure you want to delete the brand "${brand.title}"? This action cannot be undone.`,
                'Delete',
                'Cancel',
                () => handleDelete(brand.id)
              )}
              className="p-2.5 rounded-xl transition-all duration-300 hover:shadow-lg group/delete focus:outline-none"
              style={{ 
                color: currentTheme.error,
                backgroundColor: `${currentTheme.error}10`,
              }}
              title="Delete brand"
            >
              <FiTrash2 size={18} className="group-hover/delete:rotate-12 transition-transform" />
            </button>
            <button
              onClick={() => handleViewBrand(brand)}
              className="p-2.5 rounded-xl transition-all duration-300 hover:shadow-lg group/view focus:outline-none"
              style={{ 
                color: currentTheme.primary,
                backgroundColor: `${currentTheme.primary}10`,
              }}
              title="View details"
            >
              <FiEye size={18} className="group-hover/view:scale-110 transition-transform" />
            </button>
          </div>
        );
      },
    }),
  ];

  // TanStack Table instance
  const table = useReactTable({
    data: filteredBrands,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: totalPages,
  });

  // Get sorted rows, then paginate manually
  const sortedRows = table.getRowModel().rows;
  const paginatedRows = sortedRows.slice(startIndex, endIndex);

  // Stats
  const stats = {
    total: brands.length,
    withLocation: brands.filter(b => b.location).length,
    withDescription: brands.filter(b => b.description).length,
  };

  return (
    <div className="min-h-screen p-4 md:p-6 overflow-x-hidden" style={{ backgroundColor: currentTheme.background }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Brand Management
          </h1>
          <p className="text-sm" style={{ color: currentTheme.textSecondary }}>
            Manage and monitor all brand profiles
          </p>
        </div>

        {/* Stats Cards */}
        {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <StatCard 
            title="Total Brands"
            value={stats.total}
            icon={<FaBuilding />}
            color="from-blue-500 to-blue-600"
            theme={currentTheme}
          />
          <StatCard 
            title="With Location"
            value={stats.withLocation}
            icon={<FaMapMarkerAlt />}
            color="from-green-500 to-green-600"
            theme={currentTheme}
          />
          <StatCard 
            title="With Description"
            value={stats.withDescription}
            icon={<FaInfo />}
            color="from-purple-500 to-purple-600"
            theme={currentTheme}
          />
        </div> */}

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: currentTheme.textSecondary }} />
            <input
              type="text"
              placeholder="Search brands by name or location..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl focus:outline-none transition-all duration-300 focus:ring-2"
              style={{ 
                backgroundColor: currentTheme.surface, 
                color: currentTheme.text, 
                border: `1px solid ${currentTheme.border}`,
                boxShadow: `0 4px 12px ${currentTheme.primary}10`
              }}
            />
          </div>
        </div>

        {/* Brand List Table */}
        <div className="rounded-2xl shadow-2xl overflow-hidden transition-all hover:shadow-3xl"
             style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}>
          <div className="overflow-x-auto overflow-y-hidden">
            <table className="w-full">
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id} style={{ background: `linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.primary}DD)` }}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                        className={`px-4 py-4 text-left font-semibold text-sm ${header.column.getCanSort() ? 'cursor-pointer select-none hover:bg-white/10' : ''}`}
                        style={{ color: currentTheme.inverseText }}
                      >
                        <div className="flex items-center gap-2">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <span className="flex flex-col text-[10px] leading-none">
                              <span style={{ opacity: header.column.getIsSorted() === 'asc' ? 1 : 0.5 }}>▲</span>
                              <span style={{ opacity: header.column.getIsSorted() === 'desc' ? 1 : 0.5 }}>▼</span>
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {paginatedRows.length > 0 ? (
                  paginatedRows.map((row, index) => (
                    <tr
                      key={row.id}
                      className="group transition-all duration-300 hover:bg-opacity-80"
                      style={{ 
                        backgroundColor: index % 2 === 0 ? currentTheme.surface : currentTheme.card,
                        borderBottom: `1px solid ${currentTheme.border}`
                      }}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="px-4 py-3" style={{ color: currentTheme.text }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="p-12 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500/20 to-purple-500/20 flex items-center justify-center">
                          <FaBuilding className="text-3xl" style={{ color: currentTheme.primary }} />
                        </div>
                        <h3 className="text-xl font-semibold" style={{ color: currentTheme.text }}>
                          {brands.length === 0 ? "No Brands Found" : "No Matching Results"}
                        </h3>
                        <p style={{ color: currentTheme.textSecondary }}>
                          {brands.length === 0 
                            ? "There are no brands to display at the moment." 
                            : "Try adjusting your search to find what you're looking for."}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredBrands.length > 0 && (
            <div className="flex items-center justify-between px-6 py-4 border-t" style={{ borderColor: currentTheme.border }}>
              <div className="flex items-center gap-4">
                <select
                  value={rowsPerPage}
                  onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                  className="px-3 py-2 rounded-xl border focus:outline-none focus:ring-2 transition-all cursor-pointer"
                  style={{
                    backgroundColor: currentTheme.surface,
                    color: currentTheme.text,
                    borderColor: currentTheme.border
                  }}
                >
                  {[10, 25, 50, 100].map(value => (
                    <option key={value} value={value}>{value} per page</option>
                  ))}
                </select>
                <span className="text-sm" style={{ color: currentTheme.textSecondary }}>
                  Showing <span className="font-semibold" style={{ color: currentTheme.text }}>{startIndex + 1}</span> to{' '}
                  <span className="font-semibold" style={{ color: currentTheme.text }}>{Math.min(endIndex, filteredBrands.length)}</span> of{' '}
                  <span className="font-semibold" style={{ color: currentTheme.text }}>{filteredBrands.length}</span> brands
                </span>
              </div>
              <Pagination 
                currentPage={currentPage} 
                totalPages={totalPages} 
                onPageChange={handlePageChange} 
              />
            </div>
          )}
        </div>

        {/* View Brand Modal */}
        {viewBrand && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
            <div className="rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden animate-in fade-in-0 zoom-in-95 duration-300"
                 style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}>
              
              {/* Header */}
              <div className="px-6 py-4 border-b" style={{ background: `linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.primary}DD)`, borderColor: currentTheme.border }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <FaBuilding size={20} className="text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-white">Brand Details</h3>
                  </div>
                  <button
                    onClick={() => setViewBrand(null)}
                    className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <FaTimes size={20} className="text-white" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  
                  {/* Left Column - Image */}
                  <div className="lg:col-span-1">
                    <div className="rounded-xl overflow-hidden shadow-xl" style={{ backgroundColor: currentTheme.card }}>
                      <div className="relative group">
                        {imageErrors[viewBrand.id] ? (
                          <div className="w-full h-64 flex items-center justify-center" style={{ backgroundColor: `${currentTheme.primary}10` }}>
                            <FaImage className="text-6xl" style={{ color: currentTheme.primary }} />
                          </div>
                        ) : (
                          <img
                            src={viewBrand.logo}
                            alt={viewBrand.title}
                            className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                            onError={() => handleImageError(viewBrand.id)}
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      </div>
                      
                      <div className="p-4">
                        <button
                          className="w-full px-4 py-3 rounded-xl transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2 text-sm font-medium group/btn"
                          style={{ 
                            backgroundColor: `${currentTheme.primary}10`,
                            color: currentTheme.primary,
                            border: `1px solid ${currentTheme.primary}30`
                          }}
                          onClick={() => {
                            if (viewBrand?.id) {
                              fetchBrandDocuments(viewBrand.id);
                              setIsViewDocumentsModalOpen(true);
                            }
                          }}
                        >
                          <FaFile size={16} className="group-hover/btn:scale-110 transition-transform" />
                          View Documents
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Details */}
                  <div className="lg:col-span-2 space-y-4">
                    
                    {/* Basic Info Card */}
                    <InfoCard title="Basic Information" icon={<FaBuilding />} theme={currentTheme}>
                      <div className="grid grid-cols-2 gap-4">
                        <InfoItem 
                          label="Brand Name"
                          value={viewBrand.title}
                          theme={currentTheme}
                        />
                        <InfoItem 
                          label="Location"
                          value={viewBrand.location}
                          theme={currentTheme}
                          icon={<FaMapMarkerAlt />}
                        />
                      </div>
                    </InfoCard>

                    {/* Description Card */}
                    {viewBrand.description && (
                      <InfoCard title="Description" icon={<FaInfo />} theme={currentTheme}>
                        <p className="text-sm leading-relaxed" style={{ color: currentTheme.text }}>
                          {viewBrand.description}
                        </p>
                      </InfoCard>
                    )}

                    {/* Additional Info Card */}
                    <InfoCard title="Additional Information" icon={<FaTag />} theme={currentTheme}>
                      <div className="grid grid-cols-2 gap-4">
                        <InfoItem 
                          label="District"
                          value={viewBrand.district || 'Not provided'}
                          theme={currentTheme}
                        />
                        <InfoItem 
                          label="State"
                          value={viewBrand.state || 'Not provided'}
                          theme={currentTheme}
                        />
                        <InfoItem 
                          label="Pincode"
                          value={viewBrand.pincode || 'Not provided'}
                          theme={currentTheme}
                        />
                        <InfoItem 
                          label="Created On"
                          value={new Date(viewBrand.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                          theme={currentTheme}
                          icon={<FaCalendar />}
                        />
                      </div>
                    </InfoCard>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t flex justify-end" style={{ borderColor: currentTheme.border, backgroundColor: currentTheme.card }}>
                <button
                  onClick={() => setViewBrand(null)}
                  className="px-6 py-2.5 rounded-xl font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg"
                  style={{ 
                    backgroundColor: currentTheme.surface,
                    color: currentTheme.text,
                    border: `1px solid ${currentTheme.border}`
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Documents Modal */}
        {isViewDocumentsModalOpen && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4">
            <div className="rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden animate-in fade-in-0 zoom-in-95 duration-300"
                 style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}>
              
              <div className="px-6 py-4 border-b" style={{ background: `linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.primary}DD)`, borderColor: currentTheme.border }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <FaFile size={20} className="text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-white">Brand Documents</h3>
                  </div>
                  <button
                    onClick={() => setIsViewDocumentsModalOpen(false)}
                    className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <FaTimes size={20} className="text-white" />
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                {loadingDocuments ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-12 h-12 border-3 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p style={{ color: currentTheme.textSecondary }}>Loading documents...</p>
                  </div>
                ) : brandDocuments.length > 0 ? (
                  <div className="space-y-3">
                    {brandDocuments.map((doc) => (
                      <div
                        key={doc.id}
                        className="group/doc relative rounded-xl p-4 transition-all duration-300 hover:shadow-lg hover:scale-[1.02]"
                        style={{ 
                          backgroundColor: currentTheme.card,
                          border: `1px solid ${currentTheme.border}`
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-3 rounded-xl" style={{ backgroundColor: `${currentTheme.primary}15` }}>
                              <FaFile className="text-xl" style={{ color: currentTheme.primary }} />
                            </div>
                            <div>
                              <h4 className="text-sm font-semibold" style={{ color: currentTheme.text }}>{doc.name}</h4>
                              <p className="text-xs mt-1" style={{ color: currentTheme.textSecondary }}>
                                Uploaded on {new Date(doc.uploaded_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => window.open(process.env.REACT_APP_PF + doc.document, '_blank')}
                            className="p-3 rounded-xl transition-all duration-300 hover:scale-110 group/btn"
                            style={{ 
                              backgroundColor: `${currentTheme.primary}10`,
                              color: currentTheme.primary
                            }}
                            title="View Document"
                          >
                            <FiDownload size={18} className="group-hover/btn:translate-y-1 transition-transform" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r from-blue-500/20 to-purple-500/20 flex items-center justify-center">
                      <FaFile className="text-3xl" style={{ color: currentTheme.primary }} />
                    </div>
                    <h4 className="text-lg font-semibold mb-2" style={{ color: currentTheme.text }}>No Documents Found</h4>
                    <p style={{ color: currentTheme.textSecondary }}>This brand hasn't uploaded any documents yet.</p>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t flex justify-end" style={{ borderColor: currentTheme.border, backgroundColor: currentTheme.card }}>
                <button
                  onClick={() => setIsViewDocumentsModalOpen(false)}
                  className="px-6 py-2.5 rounded-xl font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg"
                  style={{ 
                    backgroundColor: currentTheme.surface,
                    color: currentTheme.text,
                    border: `1px solid ${currentTheme.border}`
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper Components
interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  theme: any;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, theme }) => (
  <div className="group/stat relative overflow-hidden rounded-2xl p-6 transition-all duration-300 hover:shadow-2xl"
       style={{ 
         background: `linear-gradient(135deg, ${theme.surface}, ${theme.card})`,
         border: `1px solid ${theme.border}`
       }}>
    <div className={`absolute inset-0 bg-gradient-to-r ${color} opacity-0 group-hover/stat:opacity-5 transition-opacity duration-500`}></div>
    <div className="relative">
      <div className="flex items-center justify-between mb-3">
        <div className={`p-3 rounded-xl bg-gradient-to-r ${color} bg-opacity-10`}>
          <div className="text-white">{icon}</div>
        </div>
        <span className="text-3xl font-bold" style={{ color: theme.text }}>{value}</span>
      </div>
      <p className="text-sm" style={{ color: theme.textSecondary }}>{title}</p>
    </div>
  </div>
);

interface InfoCardProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  theme: any;
}

const InfoCard: React.FC<InfoCardProps> = ({ title, icon, children, theme }) => (
  <div className="rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg"
       style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}>
    <div className="px-4 py-3 border-b" style={{ borderColor: theme.border, backgroundColor: `${theme.primary}10` }}>
      <h4 className="font-semibold flex items-center gap-2" style={{ color: theme.primary }}>
        {icon}
        {title}
      </h4>
    </div>
    <div className="p-4">
      {children}
    </div>
  </div>
);

interface InfoItemProps {
  label: string;
  value: string;
  theme: any;
  icon?: React.ReactNode;
}

const InfoItem: React.FC<InfoItemProps> = ({ label, value, theme, icon }) => (
  <div className="space-y-1">
    <p className="text-xs" style={{ color: theme.textSecondary }}>{label}</p>
    <p className="text-sm font-medium flex items-center gap-2" style={{ color: theme.text }}>
      {icon && <span style={{ color: theme.primary }}>{icon}</span>}
      {value}
    </p>
  </div>
);

export default AdminBrands;